import { useState, useEffect } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { ScenarioConversation } from "@common/domain/conversation-results";
import { catCode, categoryRates, describeScenarioFilters, failRate, filterScenarios, human, pct, severityOf, type ScenarioFilters, type ReviewStatus } from "./resultsLogic";
import { Panel, RateRow, ReviewCount, SeverityChip, StatTile } from "./shared";

interface Props {
  scenarios: ScenarioConversation[];
  filters: ScenarioFilters;
  onFilters: (f: ScenarioFilters) => void;
  /** Reviewers per conversation, counting everyone (reviews themselves are blind). */
  reviewStatus: (conversationId: string) => ReviewStatus;
  selectedId: string | null;
  onOpen: (id: string) => void;
}

export default function ScenarioReport({ scenarios: base, filters, onFilters, reviewStatus, selectedId, onOpen }: Props) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const [searchInput, setSearchInput] = useState(filters.query);
  useEffect(() => setSearchInput(filters.query), [filters.query]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.query) {
        onFilters({ ...filters, query: searchInput });
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchInput, filters, onFilters]);

  const { n, f } = failRate(base);
  const major = base.filter((s) => s.result === "Fail" && s.severity !== "minor").length;
  const harmful = base.filter((s) => s.mode === "harmful_compliance").length;
  const infra = base.filter((s) => s.infra).length;
  const turnCounts = [...new Set(base.map((s) => s.turnCount))].join("/");
  const byCat = categoryRates(base);
  const fails = base.filter((s) => s.result === "Fail");
  const modes = Object.entries(fails.reduce<Record<string, number>>((m, s) => ({ ...m, [s.mode]: (m[s.mode] || 0) + 1 }), {})).sort((a, b) => b[1] - a[1]);
  const areas = [...new Set(base.map((s) => s.area))].map((a) => ({ name: human(a), ...failRate(base.filter((s) => s.area === a)) })).sort((a, b) => b.f / b.n - a.f / a.n);
  const list = filterScenarios(base, filters);
  const paginatedList = list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const filterLabel = describeScenarioFilters(filters);
  const sevColor = { major: theme.palette.error.main, minor: theme.palette.warning.main, pass: "transparent" };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}>
        <TextField
          type="search" size="small" label="Search" placeholder="Scenario, transcript or judge reasoning" sx={{ flex: "1 1 360px" }}
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="scen-result">Result</InputLabel>
          <Select labelId="scen-result" label="Result" value={filters.result} onChange={(e) => onFilters({ ...filters, result: e.target.value as ScenarioFilters["result"] })}>
            <MenuItem value="*">All</MenuItem><MenuItem value="Fail">Fail</MenuItem><MenuItem value="Pass">Pass</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="scen-cat">BRI category</InputLabel>
          <Select labelId="scen-cat" label="BRI category" value={filters.cat} onChange={(e) => onFilters({ ...filters, cat: e.target.value })}>
            <MenuItem value="*">All</MenuItem>
            {[...byCat].sort((a, b) => Number(a.cat) - Number(b.cat)).map((c) => <MenuItem key={c.cat} value={c.cat}>{catCode(c.cat)} · {c.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      {filterLabel && <Typography variant="body2" color="text.secondary">{filterLabel}</Typography>}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 2 }}>
        <StatTile k="Scenarios" v={n} d={`${byCat.length} BRI categories · ${turnCounts} turns each`} />
        <StatTile k="Held" v={pct(n ? (n - f) / n : null, 1)} tone={theme.palette.secondary.main} d={
          <>{n - f} passed
            <Box sx={{ height: 6, display: "flex", gap: "2px", mt: 1 }}>
              <Box sx={{ flex: n - f, bgcolor: "secondary.main", borderRadius: 1 }} />
              <Box sx={{ flex: f, bgcolor: "error.main", borderRadius: 1 }} />
            </Box>
          </>} />
        <StatTile k="Failures" v={f} d={`${pct(n ? f / n : null, 1)} of scenarios`} tone={f ? theme.palette.error.main : theme.palette.secondary.main} />
        <StatTile k="Major" v={major} d={`${f - major} minor`} tone={major ? theme.palette.error.main : undefined} />
        <StatTile k="Harmful compliance" v={harmful} d="persona produced unsafe content" tone={harmful ? theme.palette.error.main : theme.palette.secondary.main} />
        <StatTile k="Infra errors" v={infra} d={infra ? "check target/judge logs" : "all conversations completed"} tone={infra ? theme.palette.warning.main : undefined} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.4fr) minmax(0,1fr)" }, gap: 2 }}>
        <Panel title="Failure rate by BRI category" note="Select a row to filter">
          <Box sx={{ display: "grid", gap: 0.25 }}>
            {byCat.map((c) => (
              <RateRow key={c.cat} code={catCode(c.cat)} name={c.name} n={c.n} f={c.f} selected={filters.cat === c.cat}
                onClick={() => onFilters({ ...filters, cat: filters.cat === c.cat ? "*" : c.cat })} />
            ))}
          </Box>
        </Panel>
        <Panel title="Failure modes" note="Share of failures">
          <Box sx={{ display: "grid", gap: 0.25 }}>
            {modes.length ? modes.map(([m, c]) => (
              <RateRow key={m} name={human(m)} n={fails.length} f={c} color={m === "harmful_compliance" ? theme.palette.error.main : theme.palette.primary.main} />
            )) : <Typography variant="body2" color="text.secondary">No failures.</Typography>}
          </Box>
          <Typography sx={{ fontWeight: 500, mt: 1 }}>Failure rate by test area</Typography>
          <Box sx={{ display: "grid", gap: 0.25 }}>{areas.map((a) => <RateRow key={a.name} {...a} />)}</Box>
        </Panel>
      </Box>

      <Panel title="Scenarios" note={`${list.length} of ${base.length} · Reviews: ✓ = reviewed by you`} flush>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Result</TableCell><TableCell>Scenario</TableCell><TableCell>BRI</TableCell><TableCell>Failure mode</TableCell><TableCell>Area</TableCell><TableCell align="right">Reviews</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.length ? paginatedList.map((s) => {
              const sev = severityOf(s);
              return (
                <TableRow
                  key={s.id} hover tabIndex={0} selected={selectedId === s.id} onClick={() => onOpen(s.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(s.id); } }}
                  sx={{ cursor: "pointer", "& td:first-of-type": { boxShadow: `inset 3px 0 0 ${sevColor[sev]}` }, "&.Mui-selected": { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}><SeverityChip severity={sev} /></TableCell>
                  <TableCell>
                    <div>{s.label}</div>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{s.scenarioId}</Typography>
                  </TableCell>
                  <TableCell><Box component="span" sx={{ fontFamily: "monospace", mr: 0.75 }}>{catCode(s.cat)}</Box><Typography variant="caption" color="text.secondary">{s.catName}</Typography></TableCell>
                  <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{sev !== "pass" ? s.mode : "–"}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap", color: "text.secondary" }}>{s.area}</TableCell>
                  <TableCell align="right"><ReviewCount {...reviewStatus(s.id)} /></TableCell>
                </TableRow>
              );
            }) : (
              <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No scenarios match this search.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={list.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Panel>
    </Box>
  );
}
